import React, { useEffect } from "react";
import { CreditCard, Globe, Activity, ShieldCheck } from "lucide-react";

import { ProjectHero } from "../ui/ProjectHero/ProjectHero";
import { VisualShowcase } from "../ui/VisualShowcase/VisualShowcase";
import { ProjectOverview } from "../ui/ProjectOverview/ProjectOverview";
import { BulletItem, BulletList } from "../ui/Bullets/Bullets";
import { VideoEmbed } from "../ui/VideoEmbed/VideoEmbed";
import { KeyFeatures } from "../ui/KeyFeatures/KeyFeatures";
import { ProcessSection } from "../ui/ProcessSection/ProcessSection";
import { ProjectFooter } from "../ui/ProjectFooter/ProjectFooter";
import Footer from "../../../container/InlineFooter/InlineFooter";

import images from "../../../constants/images";

import idealConcept from "../../../assets/videos/ideal.mp4";

import "./Ideal.scss";

const Ideal = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const bulletsListLeft = (
    <BulletList>
      <BulletItem>
        Integrate the bank securely into the iDEAL network
      </BulletItem>
      <BulletItem>
        Allow NN customers to initiate payments directly from their accounts
      </BulletItem>
      <BulletItem>
        Ensure compliance with Dutch financial regulations and payment standards
      </BulletItem>
      <BulletItem>
        Seamlessly integrate with web, mobile, and in-branch payment workflows
      </BulletItem>
    </BulletList>
  );

  const bulletsListRight = (
    <BulletList>
      <BulletItem>
        Implementing the backend APIs to communicate with the iDEAL payment
        network
      </BulletItem>
      <BulletItem>
        Ensuring secure authentication and authorization for customer accounts
      </BulletItem>
      <BulletItem>
        Building robust logging, monitoring, and error handling for payment
        flows
      </BulletItem>
      <BulletItem>
        Coordinating with internal product teams to integrate iDEAL payments
        across web and mobile applications
      </BulletItem>
    </BulletList>
  );

  return (
    <div className="main-project-container">
      <ProjectHero
        title="Integrating iDEAL Payments"
        subtitle="Making payments easier, faster, and safer for NN Bank customers via the Netherlands’ leading online payment method."
        client="Nationale Nederlanden"
        role="Senior Software Engineer – Payments & Accounts Team"
        year="2020 - 2022"
        platform="Mobile & Web"
        technologies={[
          "AWS",
          " GraphQL",
          " Node.js",
          " React.js",
          " React Native.js",
          " Serverless Framework",
        ]}
        category="ENGINEERING"
      />
      <VisualShowcase
        imageUrl={images.nn_building}
        imageAlt="Nationale Nederland Building"
        layout="full"
      />
      <ProjectOverview
        challengeContent={[
          "Enabling iDEAL payments within NN Bank’s existing systems",
          "NN Bank’s existing infrastructure did not yet support iDEAL as an Issuer. The challenge was to:",
        ]}
        bulletsLeft={bulletsListLeft}
        extraChallengeContent={[
          "This was not just a backend integration — it required careful coordination with payment networks, compliance teams, and internal product stakeholders.",
        ]}
        solutionContent={[
          "Full integration of iDEAL as a payment platform for NN Bank",
          "We implemented NN Bank as an iDEAL Issuer, enabling customers to make online payments directly from their bank accounts. The solution involved:",
        ]}
        extraSolutionContent={[
          "This solution allowed NN Bank customers to pay bills, make donations, top up phone credit, and more via iDEAL, providing a seamless and secure payment experience.",
        ]}
        bulletsRight={bulletsListRight}
        metrics={[
          {
            value: "Millions",
            label: "Consumers able to pay via iDEAL using NN accounts",
          },
          {
            value: "100% Secure",
            label: "Payment flows compliant with Dutch banking regulations",
          },
          {
            value: "Cross-channel",
            label: "Payments supported on web, mobile, and in-branch systems",
          },
          {
            value: "Live",
            label: "NN as a fully functional iDEAL Issuer on the network",
          },
        ]}
      />
      <VisualShowcase
        title="NN Bank Available as an iDEAL Issuer"
        description="Customers can select Nationale Nederlanden Bank from the list of supported iDEAL issuers, enabling secure payments directly from their NN current accounts."
        imageUrl={images.ideal_nn}
        imageAlt="iDEAL checkout showing multiple banks with Nationale Nederlanden Bank available as an issuer"
        layout="full"
        backgroundColor="black"
        descriptionColor="#9ca3af"
        titleColor="#ffffff"
      />
      <VideoEmbed
        title="How iDEAL Works"
        src={idealConcept}
        description="An explainer video outlining how iDEAL enables secure, bank-based online payments across the Netherlands, and why it has become the country’s standard payment method."
        controls={true}
      />
      <KeyFeatures
        title="iDEAL Integration at NN Bank"
        description="Key components and capabilities that made the integration secure, reliable, and customer-friendly."
        features={[
          {
            icon: CreditCard,
            title: "Secure Payment Processing",
            description:
              "All transactions are authenticated and authorized to meet banking and regulatory standards.",
          },
          {
            icon: Globe,
            title: "Network Integration",
            description:
              "NN Bank fully integrated as an iDEAL Issuer, enabling millions of customers to pay online seamlessly.",
          },
          {
            icon: Activity,
            title: "Cross-channel Payments",
            description:
              "Supported payments across web, mobile apps, and in-branch digital workflows.",
          },
          {
            icon: ShieldCheck,
            title: "Compliance and Monitoring",
            description:
              "Built-in monitoring, logging, and error handling to ensure operational reliability and regulatory compliance.",
          },
        ]}
      />
      <ProcessSection
        title="Integrating iDEAL for NN Bank Customers"
        description="A structured approach combining backend implementation, compliance alignment, and product delivery."
        phases={[
          {
            number: "01",
            title: "Requirements & Compliance Alignment",
            description:
              "Worked with product, legal, and compliance teams to define integration requirements and ensure regulatory adherence.",
            deliverables: [
              "Requirements documentation",
              "Compliance checklists",
              "Stakeholder alignment workshops",
              "Integration plan",
            ],
          },
          {
            number: "02",
            title: "Backend API Design & Development",
            description:
              "Implemented secure APIs to communicate with the iDEAL network and NN Bank systems.",
            deliverables: [
              "API specifications",
              "Authentication & authorization flows",
              "Error handling and logging",
              "Testing frameworks",
            ],
          },
          {
            number: "03",
            title: "Integration & Testing",
            description:
              "Connected NN Bank systems with iDEAL network endpoints and validated all customer payment flows.",
            deliverables: [
              "Integration testing",
              "Load and reliability tests",
              "QA & UAT validation",
              "Bug fixes and refinements",
            ],
          },
          {
            number: "04",
            title: "Launch & Operational Support",
            description:
              "Rolled out iDEAL payments to NN customers with monitoring, support, and incident management processes in place.",
            deliverables: [
              "Go-live support",
              "Monitoring dashboards",
              "Incident response procedures",
              "Ongoing operational maintenance",
            ],
          },
        ]}
      />
      <ProjectFooter
        impactDescription="The iDEAL integration enabled Nationale Nederlanden Bank to operate as a fully functional Issuer, allowing customers to make secure online payments directly from their NN current accounts. By connecting NN Bank to the country’s most widely used payment method, the project improved customer convenience, expanded payment coverage, and positioned the bank as a modern, digitally capable financial institution."
        impactLabel="Project Impact"
        impactTitle="Bringing NN Bank onto the iDEAL payment network"
        nextProject={{ title: "KFC Menu Generation", link: "/project/kfc" }}
        conclusion={{
          label: "Design / Engineering Reflection",
          title: "Payments infrastructure is trust infrastructure",
          description:
            "Integrating iDEAL reinforced that payment systems are not just technical integrations — they are trust-critical infrastructure. Success depended on aligning engineering, compliance, and product, while building systems that prioritise security, reliability, and operational clarity over novelty.",
        }}
      />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Footer />
      </div>
    </div>
  );
};

export { Ideal };

/*

Project Context

iDEAL is the standard online payment method in the Netherlands since 2005, allowing consumers to pay directly via their bank accounts for everything from energy bills and donations to municipal taxes and phone top-ups.

As the most widely used payment system in the country, iDEAL requires banks to act as Issuers on the network to allow customers to pay online securely. Nationale Nederlanden Bank (NN) wanted to enable its customers to pay using iDEAL through their NN current accounts.


My Role

Senior Software Engineer – Payments & Accounts Team

I worked as part of NN Bank’s Payments and Accounts team to deliver the iDEAL integration. My responsibilities included:

Implementing backend services and APIs for iDEAL payment processing

Integrating NN Bank’s systems with the iDEAL payment network

Ensuring compliance with financial regulations and payment standards

Coordinating with product, compliance, and UX teams to deliver a smooth customer experience

Building monitoring and testing frameworks for operational reliability

*/
